import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  Menu,
  MenuItem,
  Avatar,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Refresh,
  Edit,
  Delete,
  MoreVert,
  ArrowBack,
  MiscellaneousServices,
  Search
} from '@mui/icons-material';
import {
  getServiceOptions,
  deleteServiceOption
} from '../../../services/Api';
import DeleteConfirm from '../../../modals/DeleteConfirm';

const DisplayServiceOptions = () => {
  const { subServiceId } = useParams();
  const navigate = useNavigate();

  const [serviceOptions, setServiceOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSelectedOption, setMenuSelectedOption] = useState(null);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchServiceOptions = async (page = 1, limit = 10, search = '') => {
    try {
      setLoading(true);
      setError(null);

      const response = await getServiceOptions(subServiceId, page, limit, search);

      if (response.success) {
        setServiceOptions(response.data || []);
        setTotalRecords(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || response.totalRecords || 0 || 0);
      }
    } catch (err) {
      console.error('Error fetching service options:', err);
      setError(err.response?.data?.message || 'Failed to fetch service options');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceOptions(1, 10, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subServiceId, debouncedSearch]);

  const handleMenuOpen = (event, option) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedOption(option);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSelectedOption(null);
  };

  const handleDeleteClick = () => {
    setOptionToDelete(menuSelectedOption.id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!optionToDelete) return;
    try {
      setLoading(true);
      setError(null);
      const response = await deleteServiceOption(optionToDelete);
      if (response.success) {
        setDeleteDialogOpen(false);
        setOptionToDelete(null);
        fetchServiceOptions();
      } else {
        setError(response.message || 'Failed to delete service option');
        setDeleteDialogOpen(false);
        setOptionToDelete(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred while deleting the service option');
      setDeleteDialogOpen(false);
      setOptionToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchServiceOptions(newPage + 1, rowsPerPage, debouncedSearch);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchServiceOptions(1, newRowsPerPage, debouncedSearch);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setPage(0);
    setServiceOptions([]);
    fetchServiceOptions(1, rowsPerPage, '');
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/services')}
            sx={{ color: 'primary.main' }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 300, color: 'text.primary' }}>
              Service Options
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and view service options for sub-category
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search by name..."
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              width: 180,
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                bgcolor: 'rgba(0,0,0,0.02)'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
          />
          <IconButton onClick={handleRefresh} disabled={loading} sx={{ color: 'primary.main' }}>
            {loading ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate(`/services/service-options/create/${subServiceId}`)}
            sx={{ px: 4, backgroundColor: 'primary.main' }}
          >
            Add Service Option
          </Button>
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="service options table">
            <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Discount Price</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Discount %</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Updated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && serviceOptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : serviceOptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                    <MiscellaneousServices sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                    <Typography color="text.secondary">
                      {searchTerm ? 'No service options found matching your search' : 'No service options found'}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/services/service-options/create/${subServiceId}`)}
                      sx={{ mt: 2 }}
                    >
                      Create your first service option
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                serviceOptions.map((option) => (
                  <TableRow key={option.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={option.image}
                        sx={{ width: 60, height: 40, border: '1px solid', borderColor: 'text.border' }}
                      >
                        <MiscellaneousServices />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      {option.description && option.description !== '<p></p>' ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="div"
                          sx={{
                            '& p': { m: 0 },
                            '& ul, & ol': { pl: 2, m: 0 },
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal'
                          }}
                          dangerouslySetInnerHTML={{ __html: option.description }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, textDecoration: option.discountPrice ? 'line-through' : 'none', color: option.discountPrice ? 'text.secondary' : 'text.primary' }}>
                        {option.price ? `₹${Math.round(Number(option.price))}` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                        {option.discountPrice ? `₹${Math.round(Number(option.discountPrice))}` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                        {option.discountPercentage ? `${Math.round(Number(option.discountPercentage))}%` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {option.duration ? `${option.duration} mins` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={option.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={option.isActive ? 'success' : 'error'}
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {option.updatedAt
                          ? new Date(option.updatedAt).toLocaleDateString()
                          : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, option)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {serviceOptions.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 150,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <MenuItem onClick={() => {
          navigate(`/services/service-options/edit/${menuSelectedOption?.id}?subServiceId=${subServiceId}`);
          handleMenuClose();
        }}>
          <Edit sx={{ mr: 1, fontSize: 18 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1, fontSize: 18 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirm
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setOptionToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Service Option"
        message="Are you sure you want to delete this service option? This action cannot be undone."
      />
    </Box>
  );
};

export default DisplayServiceOptions;